import { inject, Injectable, signal } from '@angular/core'
import { DEV_OR_LOCALHOST } from '@bases/base.token'
import { HttpService } from '@bases/http.service'
import { INTEGRATION_SERVER_URL, LAYER_CLONED_TYPES } from '@core/constants/constant.data'
import {
  LayerCloned,
  LayerClonedResponse,
  LayerIntegrationHistoryApiResponse,
  LayerIntegrationPayload,
} from '@core/models/layers.model'
import Keycloak from 'keycloak-js'
import { BehaviorSubject } from 'rxjs'
@Injectable({
  providedIn: 'root',
})
export class LayersClonedService extends HttpService {
  devOrLocalhost = inject<BehaviorSubject<string>>(DEV_OR_LOCALHOST)
  private readonly _keycloak = inject(Keycloak)
  layersCloned$ = signal<LayerCloned[]>([])
  isLayersClonedLoading$ = signal<boolean>(false)

  constructor() {
    super()
    this.init()
  }

  async init() {
    this._getLayersCloned()
  }

  async refreshLayersCloned() {
    await this._getLayersCloned()
  }

  private _getHeadersIntegration() {
    return {
      Authorization: `Bearer ${this._keycloak.token}`,
    }
  }

  private async _getLayersCloned() {
    this.isLayersClonedLoading$.set(true)
    const url = INTEGRATION_SERVER_URL + 'layer-repos/layers-names'

    const headers = this._getHeadersIntegration()
    const response = await this.GETwithHeaders(url, headers)
    if (!response || !response.cloned_layers) {
      this.layersCloned$.set([])
      this.isLayersClonedLoading$.set(false)
      return
    }
    const mappedLayers = response.cloned_layers?.map((layer: LayerClonedResponse) => ({
      repo: layer.repo,
      repoUrl: layer.repo_url,
      layerName: layer.layer_name,
      layerType: layer.layer_type,
      errors: layer.errors,
    }))

    this.layersCloned$.set(mappedLayers)
    this.isLayersClonedLoading$.set(false)
  }

  async deleteRepoIntegration(repoName: string, layerName: string) {
    const urlDeleteLayer = INTEGRATION_SERVER_URL + 'layer-repos/delete/' + repoName

    const headers = this._getHeadersIntegration()
    await this.DELETEwithHeaders(urlDeleteLayer, headers)

    await this._getLayersCloned()

    const urlDeleteLayerTable = INTEGRATION_SERVER_URL + 'database/layers/' + layerName

    await this.DELETEwithHeaders(urlDeleteLayerTable, headers)
  }

  async cloneRepository(repoUrl: string) {
    const url = INTEGRATION_SERVER_URL + 'layer-repos/clone'

    const headers = this._getHeadersIntegration()
    const body = {
      repo_url: repoUrl,
    }
    await this.POSTwithHeaders(url, body, headers)

    await this._getLayersCloned()
  }

  async updateRepository(repoName: string) {
    const url = INTEGRATION_SERVER_URL + 'layer-repos/pull/' + encodeURIComponent(repoName)

    const headers = this._getHeadersIntegration()
    await this.POSTwithHeaders(url, {}, headers)

    await this._getLayersCloned()
  }

  async integrateLayer(
    repoName: string,
    layerType: string,
    adminGeomTableName: string[],
    description: string,
    style: string,
  ) {
    let url = INTEGRATION_SERVER_URL + 'integration/'
    const baseBody: LayerIntegrationPayload = {
      repo_name: repoName,
      description,
      style,
    }
    let body: LayerIntegrationPayload

    if (layerType === 'Raster') {
      url += 'complete-raster'
      body = { ...baseBody, admin_geom_table_name: adminGeomTableName }
    } else if (layerType === 'Vector') {
      url += 'complete-vector'
      body = baseBody
    } else if (layerType === 'Tabular') {
      url += 'complete-tabular'
      body = { ...baseBody, admin_geom_table_name: adminGeomTableName }
    } else {
      return
    }

    const headers = this._getHeadersIntegration()

    await this.POSTwithHeaders(url, body, headers)

    await this._getLayersCloned()
  }

  async getLayerIntegration(layerName: string) {
    const url =
      INTEGRATION_SERVER_URL + 'integration/history/layer/' + encodeURIComponent(layerName)
    const headers = this._getHeadersIntegration()
    const response = (await this.GETwithHeaders(url, headers)) as LayerIntegrationHistoryApiResponse
    if (!response) {
      this._toasterService.openSnackBar('No layer integration history found for this layer.')
      return []
    }

    return response.layers
      ?.map((layer) => ({
        id: layer.id,
        repositoryId: layer.repository_id,
        layerName: layer.layer_name,
        intoDb: layer.into_db,
        statsPrecompute: layer.stats_precompute,
        geoserverTiff: layer.geoserver_tiff,
        geoserverPyramidTiff: layer.geoserver_pyramid_tiff,
        deploymentGeoserver: layer.deployment_geoserver,
        repository: {
          repoName: layer.repository.repo_name,
          hashCommit: layer.repository.hash_commit,
          dateExecution: layer.repository.date_execution,
          repoUrl: layer.repository.repo_url,
        },
      }))
      .sort((a, b) => b.id - a.id)
  }

  filterLayersCloned(filterValue: string = '', layerTypes: string[] = []) {
    const lowerFilterValue = filterValue.toLowerCase()
    return signal(
      this.layersCloned$().filter((layer) => {
        const textFilter =
          layer.layerName.toLowerCase().includes(lowerFilterValue) ||
          layer.repo.toLowerCase().includes(lowerFilterValue)

        const typeFilter = layerTypes.length === 0 || layerTypes.includes(layer.layerType)

        return textFilter && typeFilter
      }),
    )
  }

  getLayerIntegrationTypes() {
    return LAYER_CLONED_TYPES
  }
}
